import { PlanType } from '@showbie-socrative/socrative-utils';

import { freePermission } from './simplePermissions';
import { FeaturePermission, ProCheck } from './types';

export const FEATURE_PERMISSION_SYMBOL = Symbol.for('FeaturePermissions');

const getFeatures = (): FeaturePermission =>
  window[FEATURE_PERMISSION_SYMBOL] ?? freePermission;

const userPlan = (): PlanType => getFeatures().userPlan;
const isPro = () => userPlan() === PlanType.Pro;
const hasPaidPlan = () => userPlan() !== PlanType.Free && userPlan() !== null;
const canUploadAiFiles = () =>
  [
    PlanType.Pro,
    PlanType.LegacyPro,
    PlanType.Complete,
    PlanType.Organization,
  ].includes(userPlan());
const canMergeQuizzes = () => getFeatures().mergeQuizzes;
const canMakeFolders = () => getFeatures().makeFolders;
const canRestoreTrash = () => getFeatures().restoreTrash;
const canShowAdvancedQuizEditor = () => getFeatures().showAdvancedQuizEditor;
const canShareResults = () => getFeatures().shareResults;
const canCustomizeExportResults = () => getFeatures().customizeExportResults;
const canManuallyGrade = () => getFeatures().manuallyGrade;
const canAddRoom = () => getFeatures().addRoom;
const canShareRoom = () => getFeatures().shareRoom;
const canAddRoster = () => getFeatures().addRoster;
const canLimitOneAttempt = () => getFeatures().limitOneAttempt;
const canTimeSpaceRace = () => getFeatures().timeSpaceRace;
const canCreateWorkspaceFolder = () => getFeatures().createWorkspaceFolder;
const canMoveWorkspaceFolder = () => getFeatures().moveWorkspaceFolder;
const canRenameWorkspaceFolder = () => getFeatures().renameWorkspaceFolder;
const canDeleteWorkspaceFolder = () => getFeatures().deleteWorkspaceFolder;
const canPurgeWorkspaceFolder = () => getFeatures().purgeWorkspaceFolder;
const canRestoreWorkspaceFolder = () => getFeatures().restoreWorkspaceFolder;
const canUseSharedLibraryQuizzes = () => getFeatures()?.useSharedLibraryQuizzes;
const canCreateLibrary = () => getFeatures()?.createLibrary;
const canPauseActivity = () => getFeatures()?.pauseActivity;
const canEmbedMedia = () => getFeatures()?.embedMedia ?? false;

/**
 * The purpose of this pro check is to add a layer of abstraction between the
 * `isPro` flag, and the capability that needs to be enabled or disabled.
 * Rather than asking "Are you pro" ask, "Can you restore workspace Folders?"
 *
 * This allows for the flexibility of changing how those capabilities are determined
 * and means that the features being changed, don't have to know about the underlying
 * implementation of how those permissions are determined
 */

const proCheck: ProCheck & {
  isPro: () => boolean;
  hasPaidPlan: () => boolean;
  canUploadAiFiles: () => boolean;
} = {
  isPro,
  hasPaidPlan,
  canUploadAiFiles,
  userPlan,
  canMergeQuizzes,
  canMakeFolders,
  canRestoreTrash,
  canShowAdvancedQuizEditor,
  canShareResults,
  canCustomizeExportResults,
  canManuallyGrade,
  canAddRoom,
  canShareRoom,
  canAddRoster,
  canLimitOneAttempt,
  canTimeSpaceRace,
  canCreateWorkspaceFolder,
  canMoveWorkspaceFolder,
  canRenameWorkspaceFolder,
  canDeleteWorkspaceFolder,
  canPurgeWorkspaceFolder,
  canRestoreWorkspaceFolder,
  canUseSharedLibraryQuizzes,
  canCreateLibrary,
  canPauseActivity,
  canEmbedMedia,
};

export { proCheck };
